import { Component, DestroyRef, OnInit, HostListener, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Subject, debounceTime, distinctUntilChanged, switchMap } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FaqWidgetService } from '@core/services/faq-widget.service';
import { FaqService, FaqCategory } from '@features/faq/services/faq.service';
import { AuthService } from '@core/auth/auth.service';


import { AppIconComponent } from '../../../shared/components/app-icon/app-icon.component';
@Component({
  selector: 'app-faq-widget',
  standalone: true,
  imports: [AppIconComponent,
    CommonModule, FormsModule
  ],
  templateUrl: './faq-widget.component.html',
  styleUrls: ['./faq-widget.component.css']
})
export class FaqWidgetComponent implements OnInit {
  private destroyRef = inject(DestroyRef);
  private searchSubject = new Subject<string>();

  isOpen = false;
  /** La languette du bord droit n'apparaît qu'une fois connecté (pas sur le login). */
  isLoggedIn = false;
  private readonly authService = inject(AuthService);
  categories: FaqCategory[] = [];
  searchQuery = '';
  searchFocused = false;
  isLoading = false;
  loaded = false;              // lazy: only call API on first open
  expandedCategories = new Set<number>();
  expandedItems = new Set<string>();

  constructor(
    private widgetService: FaqWidgetService,
    private faqService: FaqService
  ) {}

  ngOnInit(): void {
    this.authService.currentUser$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(user => {
        this.isLoggedIn = !!user;
        if (!user) this.widgetService.close();
      });

    this.widgetService.isOpen$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(open => {
        this.isOpen = open;
        if (open && !this.loaded) {
          this.loadFaq();
        }
      });

    this.searchSubject.pipe(
      debounceTime(300),
      distinctUntilChanged(),
      switchMap(keyword => {
        this.isLoading = true;
        return keyword.trim()
          ? this.faqService.search(keyword)
          : this.faqService.getAll();
      }),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe({
      next: data => {
        this.categories = data.sort((a, b) => a.order - b.order);
        this.isLoading = false;
        this.expandedCategories.clear();
        this.expandedItems.clear();
        this.categories.forEach((_, i) => this.expandedCategories.add(i));
        if (this.searchQuery.trim()) {
          this.categories.forEach((cat, ci) =>
            cat.items.forEach((_, ii) => this.expandedItems.add(`${ci}-${ii}`))
          );
        }
      },
      error: () => { this.isLoading = false; }
    });
  }

  private loadFaq(): void {
    this.isLoading = true;
    this.faqService.getAll()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: data => {
          this.categories = data.sort((a, b) => a.order - b.order);
          this.categories.forEach((_, i) => this.expandedCategories.add(i));
          this.isLoading = false;
          this.loaded = true;
        },
        error: () => { this.isLoading = false; this.loaded = true; }
      });
  }

  get edgeTabTitle(): string {
    return this.isOpen ? "Fermer l'aide" : "Aide en ligne — questions fréquentes";
  }

  toggle(): void { this.widgetService.toggle(); }
  close(): void  { this.widgetService.close(); }

  onSearchChange(q: string): void {
    this.searchQuery = q;
    this.searchSubject.next(q);
  }

  clearSearch(): void {
    this.searchQuery = '';
    this.searchSubject.next('');
  }

  toggleCategory(i: number): void {
    this.expandedCategories.has(i) ? this.expandedCategories.delete(i) : this.expandedCategories.add(i);
  }
  isCategoryExpanded(i: number): boolean { return this.expandedCategories.has(i); }

  toggleItem(ci: number, ii: number): void {
    const k = `${ci}-${ii}`;
    this.expandedItems.has(k) ? this.expandedItems.delete(k) : this.expandedItems.add(k);
  }
  isItemExpanded(ci: number, ii: number): boolean { return this.expandedItems.has(`${ci}-${ii}`); }

  getIconEmoji(icon: string): string {
    const map: Record<string, string> = {
      chat: '💬', record_voice_over: '🎙️', safety_check: '👷',
      description: '📝', engineering: '🏗️', gavel: '⚖️',
      directions_car: '🚗', directions_car_filled: '🚗', build: '🔧',
      inventory: '📦', lightbulb: '💡', help: '❓', info: 'ℹ️',
      school: '🎓', assignment: '📋', folder: '📁', security: '🔒',
      construction: '🏗️', settings: '⚙️', people: '👥', rule: '📏',
    };
    return map[icon] ?? '📌';
  }

  renderMarkdown(text: string): string {
    if (!text) return '';
    return text
      .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
      .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
      .replace(/\n/g, '<br>');
  }

  /** Close on Escape key */
  @HostListener('document:keydown.escape')
  onEscape(): void { if (this.isOpen) this.close(); }
}
